import { Schemas } from '@api-client/generated/types';
import { getDateDefault } from '@utils/date';

export type PlanType = Schemas.AccountingPlanType;
export type UsageStatus = Schemas.AccountingUsageStatus;

/** The backend's @Max on planLimit. */
export const MAX_PLAN_LIMIT = 1_000_000_000;

/** Thin space, the thousands separator of the design: `1 234`, `€75 000`. */
const THIN_SPACE = ' ';

export const formatInteger = (value: number): string =>
  Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE);

/** In the plan's own unit: a document count, or whole euros for a legacy turnover plan. */
export const formatPlanAmount = (
  value: number,
  planType: PlanType | null
): string =>
  planType === 'legacy_turnover'
    ? `€${formatInteger(value)}`
    : formatInteger(value);

export const PLAN_TYPE_LABEL: Record<PlanType, string> = {
  documents: 'Bookings',
  legacy_turnover: 'Legacy turnover',
};

export const STATUS_LABEL: Record<UsageStatus, string> = {
  within_plan: 'Within plan',
  near_limit: 'Near limit',
  over_plan: 'Over plan',
};

/** Ascending severity, for sorting: Within < Near < Over. */
export const STATUS_RANK: Record<UsageStatus, number> = {
  within_plan: 0,
  near_limit: 1,
  over_plan: 2,
};

/** The backend's thresholds (deriveUsage): near from 80% of the limit, over above it. */
export const statusFor = (used: number, limit: number): UsageStatus => {
  if (used > limit) return 'over_plan';
  return limit > 0 && (used / limit) * 100 >= 80 ? 'near_limit' : 'within_plan';
};

export const STATUS_COLOR: Record<UsageStatus, string> = {
  within_plan: '#1a56db',
  near_limit: '#e0a100',
  over_plan: '#d9342b',
};

/** dd.mm.yyyy, or a dash for a date that is not there (getDateDefault would print today). */
export const formatDate = (value: string | null): string =>
  value ? getDateDefault(value) : '—';
